import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "../style.module.css";
import { COPPER, TOOLTIP_STYLE } from "./theme";
import ISO_NUMERIC from "./isoNumeric";

// World choropleth: each country shaded by its value (darker = fewer, full
// copper = most). `rows` = [{ code: "IN", name: "India", value: 178 }], code
// being ISO alpha-2 as the geo API reports it.
//
// The map shapes (world-atlas, ~100 KB) and d3-geo/topojson are loaded with a
// dynamic import() the first time a map renders, so they're split into their
// own chunk instead of growing the main bundle for every CMS page.

const WIDTH = 960;
const HEIGHT = 470;
const MAX_MAP_WIDTH = 780; // px on screen; the SVG scales down below this on narrow cards
const ANTARCTICA = "010"; // dropped: huge, never has viewers, wastes a third of the height
const EMPTY_FILL = "rgba(255,255,255,0.07)";
const STROKE = "rgba(255,255,255,0.18)";

let shapesPromise = null;
const loadShapes = () => {
  if (!shapesPromise) {
    shapesPromise = Promise.all([
      import("world-atlas/countries-110m.json"),
      import("topojson-client"),
      import("d3-geo"),
    ]).then(([atlas, topojson, d3]) => {
      const world = atlas.default || atlas;
      const features = topojson
        .feature(world, world.objects.countries)
        .features.filter((f) => f.id !== ANTARCTICA);
      const collection = { type: "FeatureCollection", features };
      const projection = d3.geoNaturalEarth1().fitSize([WIDTH, HEIGHT], collection);
      const path = d3.geoPath(projection);
      return features.map((f) => ({ id: f.id, name: f.properties.name, d: path(f) }));
    });
    // Let a failed load (e.g. network blip fetching the chunk) retry next time.
    shapesPromise.catch(() => { shapesPromise = null; });
  }
  return shapesPromise;
};

const GeoMap = ({ title, rows, formatValue, valueLabel = "Views", emptyHint, asOf = true }) => {
  const [shapes, setShapes] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [hover, setHover] = useState(null); // { name, value, share, x, y }
  const wrapRef = useRef(null);
  const fmt = (v) => (formatValue ? formatValue(v) : Number(v).toLocaleString());

  useEffect(() => {
    let cancelled = false;
    loadShapes()
      .then((s) => { if (!cancelled) setShapes(s); })
      .catch(() => { if (!cancelled) setLoadError(true); });
    return () => { cancelled = true; };
  }, []);

  // numeric map id -> { name, value } for countries that have data
  const { byId, max, total } = useMemo(() => {
    const m = new Map();
    let mx = 0;
    let sum = 0;
    (rows || []).forEach((r) => {
      const value = Number(r.value) || 0;
      const id = ISO_NUMERIC[String(r.code || "").toUpperCase()];
      if (!id || value <= 0) return;
      const prev = m.get(id);
      const next = { name: r.name || r.code, value: (prev?.value || 0) + value };
      m.set(id, next);
      mx = Math.max(mx, next.value);
      sum += value;
    });
    return { byId: m, max: mx, total: sum };
  }, [rows]);

  // sqrt scale so one dominant country doesn't wash every other one out.
  const opacityFor = (value) => 0.25 + 0.75 * Math.sqrt(value / max);

  const showTip = (e, name, value) => {
    const box = wrapRef.current?.getBoundingClientRect();
    if (!box) return;
    setHover({
      name,
      value,
      share: total > 0 && value ? (value / total) * 100 : 0,
      x: e.clientX - box.left,
      y: e.clientY - box.top,
    });
  };

  const hasData = byId.size > 0;

  return (
    <div className={styled.chartCard}>
      <div className={styled.chartCardHead}>
        <h3 className={styled.chartTitle}>{title}</h3>
        {asOf && <span className={styled.asOfTag}>Live</span>}
      </div>

      {loadError ? (
        <p className={styled.emptyHint}>The map couldn't be loaded. Refresh the page to try again.</p>
      ) : !shapes ? (
        <p className={styled.emptyHint}>Loading map…</p>
      ) : (
        <>
          <div
            ref={wrapRef}
            // Capped and centered so a full-width card doesn't blow the map up to page height.
            style={{ position: "relative", maxWidth: MAX_MAP_WIDTH, margin: "0 auto" }}
            onMouseLeave={() => setHover(null)}
          >
            <svg
              viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
              style={{ width: "100%", height: "auto", display: "block" }}
              role="img"
              aria-label={title}
            >
              {shapes.map((s) => {
                const datum = byId.get(s.id);
                const name = datum?.name || s.name;
                return (
                  <path
                    key={s.id || s.name}
                    d={s.d}
                    fill={datum ? COPPER : EMPTY_FILL}
                    fillOpacity={datum ? opacityFor(datum.value) : 1}
                    stroke={hover?.name === name ? "rgba(255,255,255,0.85)" : STROKE}
                    strokeWidth={hover?.name === name ? 1.2 : 0.5}
                    onMouseMove={(e) => showTip(e, name, datum?.value || 0)}
                    style={{ cursor: "default", transition: "fill-opacity 120ms" }}
                  >
                    <title>{`${name}: ${fmt(datum?.value || 0)} ${valueLabel.toLowerCase()}`}</title>
                  </path>
                );
              })}
            </svg>

            {hover && (
              <div
                style={{
                  ...TOOLTIP_STYLE.contentStyle,
                  position: "absolute",
                  left: hover.x + 12,
                  top: hover.y + 12,
                  padding: "6px 10px",
                  pointerEvents: "none",
                  whiteSpace: "nowrap",
                  // keep the tooltip inside the card near the right edge
                  transform: hover.x > (wrapRef.current?.clientWidth || 0) * 0.7 ? "translateX(calc(-100% - 24px))" : undefined,
                }}
              >
                <div style={{ fontWeight: 600 }}>{hover.name}</div>
                <div style={{ opacity: 0.8 }}>
                  {hover.value > 0
                    ? `${valueLabel}: ${fmt(hover.value)} (${hover.share.toFixed(1)}%)`
                    : `No ${valueLabel.toLowerCase()} in this range`}
                </div>
              </div>
            )}
          </div>

          {hasData ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: 8, marginTop: 10, fontSize: 11, color: "rgba(255,255,255,0.6)" }}>
              <span>Fewer {valueLabel.toLowerCase()}</span>
              <span
                style={{
                  width: 120,
                  height: 8,
                  borderRadius: 4,
                  background: `linear-gradient(90deg, ${COPPER}40, ${COPPER})`,
                }}
              />
              <span>More (max {fmt(max)})</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, marginLeft: 12 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: EMPTY_FILL, border: `1px solid ${STROKE}` }} />
                No data
              </span>
            </div>
          ) : (
            <p className={styled.emptyHint}>{emptyHint || "No geo data yet."}</p>
          )}
        </>
      )}
    </div>
  );
};

export default GeoMap;
